import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Button } from '@/shared/components/ui/Button';
import {
  CheckCircle2, Landmark, AlertTriangle, Clock, X, Loader2, Plus, Search,
  ArrowDownLeft, ArrowUpRight
} from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';
import { localToday } from '@/shared/utils/utils';

const INPUT = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100 transition-colors';
const LABEL = 'block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5';

type Direction = 'receivable' | 'payable';

interface Pdc {
  _id: string;
  direction: Direction;
  docNo: string;
  chequeNo: string;
  chequeDate: string;
  bankName: string;
  partyName: string;
  amount: number;
  status: 'pending' | 'presented' | 'cleared' | 'bounced' | 'cancelled';
  narration: string;
}

interface Party { _id: string; displayName: string; legalName: string; isCustomer?: boolean; isSupplier?: boolean; }

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    presented: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    cleared: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    bounced: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
    cancelled: 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
  };
  return (
    <span className={`uppercase text-[10px] px-2.5 py-0.5 rounded-full font-bold ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

function isOverdue(pdc: Pdc) {
  return (pdc.status === 'pending' || pdc.status === 'presented') && new Date(pdc.chequeDate) < new Date(new Date().toDateString());
}

function CreatePdcModal({ direction, onClose, onCreated }: { direction: Direction; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    chequeNo: '', chequeDate: localToday(), bankName: '',
    partyId: '', amount: '', narration: '',
  });
  const [parties, setParties] = useState<Party[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get(`/api/accounting/parties?role=${direction === 'receivable' ? 'customer' : 'supplier'}`)
      .then((res) => {
        const list = Array.isArray(res.data.data) ? res.data.data : res.data.data?.items || [];
        setParties(list);
      })
      .catch(() => setParties([]));
  }, [direction]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountVal = parseFloat(form.amount);
    if (!amountVal || amountVal <= 0) { setError('Amount must be greater than zero.'); return; }
    if (!form.partyId) { setError(`Select a ${direction === 'receivable' ? 'customer' : 'supplier'}.`); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post('/api/accounting/pdc', { direction, ...form, amount: amountVal });
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Could not save this cheque.');
    } finally {
      setSaving(false);
    }
  };

  const isReceivable = direction === 'receivable';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline">
          <div className={`flex items-center gap-2 ${isReceivable ? 'text-success-text' : 'text-primary'}`}>
            <Landmark className="w-5 h-5" />
            <h2 className="font-display text-[15px] font-semibold text-on-surface">Record {isReceivable ? 'Receivable' : 'Payable'} PDC</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-on-surface-variant" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-lg p-2.5">{error}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>{isReceivable ? 'Customer' : 'Supplier'} *</label>
              <select required className={INPUT} value={form.partyId} onChange={(e) => setForm((f) => ({ ...f, partyId: e.target.value }))}>
                <option value="">Select…</option>
                {parties.map((p) => <option key={p._id} value={p._id}>{p.displayName || p.legalName}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Cheque No *</label>
              <input required className={INPUT} value={form.chequeNo} onChange={(e) => setForm((f) => ({ ...f, chequeNo: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Cheque Date *</label>
              <input required type="date" className={INPUT} value={form.chequeDate} onChange={(e) => setForm((f) => ({ ...f, chequeDate: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Bank Name</label>
              <input className={INPUT} value={form.bankName} onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} />
            </div>
            <div>
              <label className={LABEL}>Amount (₹) *</label>
              <input required type="number" step="0.01" min="0.01" className={INPUT + ' font-mono'} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Narration</label>
              <input className={INPUT} placeholder="Optional note" value={form.narration} onChange={(e) => setForm((f) => ({ ...f, narration: e.target.value }))} />
            </div>
          </div>
          {isReceivable && (
            <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 text-xs text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900">
              Booked immediately: Dr Undeposited Funds / Cr Accounts Receivable — clearing later moves it into Bank.
            </div>
          )}
          {!isReceivable && (
            <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-lg p-3 text-xs text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900">
              No ledger entry yet — a payable cheque only posts (Dr Accounts Payable / Cr Bank) once it actually clears.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Cheque
          </button>
        </div>
      </form>
    </div>
  );
}

function BounceModal({ pdc, onClose, onDone }: { pdc: Pdc; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/api/accounting/pdc/${pdc._id}/bounce`, { reason });
      onDone();
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Mark cheque {pdc.chequeNo} as bounced</h2>
        <div>
          <label className={LABEL}>Reason</label>
          <input className={INPUT} placeholder="e.g. Insufficient funds" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-100 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50">
            Confirm Bounce
          </button>
        </div>
      </div>
    </div>
  );
}

export function PdcPage() {
  const [direction, setDirection] = useState<Direction>('receivable');
  const [items, setItems] = useState<Pdc[]>([]);
  const [summary, setSummary] = useState<Record<Direction, { count: number; amount: number; overdueCount: number }>>({
    receivable: { count: 0, amount: 0, overdueCount: 0 },
    payable: { count: 0, amount: 0, overdueCount: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [bounceTarget, setBounceTarget] = useState<Pdc | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        api.get(`/api/accounting/pdc?direction=${direction}&limit=200`),
        api.get('/api/accounting/pdc/summary'),
      ]);
      setItems(listRes.data.data?.items || []);
      setSummary(summaryRes.data.data || summary);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  useEffect(() => { load(); }, [load]);

  const act = async (pdc: Pdc, action: 'present' | 'clear' | 'cancel') => {
    try {
      await api.post(`/api/accounting/pdc/${pdc._id}/${action}`, {});
      showToast(`Cheque ${pdc.chequeNo} ${action === 'present' ? 'presented' : action === 'clear' ? 'cleared' : 'cancelled'}.`);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    }
  };

  const q = search.toLowerCase();
  const filtered = items.filter((p) =>
    !q || p.chequeNo.toLowerCase().includes(q) || p.partyName.toLowerCase().includes(q) || p.docNo.toLowerCase().includes(q));

  return (
    <div className="">
      <AccountingSubNav />

      <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
        {toast && (
          <div className="flex items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-800 dark:text-emerald-300 text-sm shadow-sm">
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" /> {toast}
          </div>
        )}

        <PageHeader
          icon={Landmark}
          eyebrow="Accounts"
          title="Post-Dated Cheques"
          description="Cheques received and issued for a future date; clearance posts straight to the ledger."
          actions={<Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> Record Cheque</Button>}
        />

        <StatGrid cols={3}>
          {(['receivable', 'payable'] as Direction[]).map((d) => (
            <StatCard
              key={d}
              label={d === 'receivable' ? 'Receivable — in hand' : 'Payable — issued'}
              value={formatINR(summary[d].amount)}
              hint={`${summary[d].count} pending${summary[d].overdueCount ? ` · ${summary[d].overdueCount} overdue` : ''}`}
              icon={d === 'receivable' ? ArrowDownLeft : ArrowUpRight}
              tone={d === 'receivable' ? 'success' : 'primary'}
              onClick={() => setDirection(d)}
              active={direction === d}
            />
          ))}
          <StatCard
            label="Overdue — both directions"
            value={summary.receivable.overdueCount + summary.payable.overdueCount}
            hint="Past cheque date, still open"
            icon={AlertTriangle}
            tone={summary.receivable.overdueCount + summary.payable.overdueCount > 0 ? 'danger' : 'neutral'}
          />
        </StatGrid>

        {/* Maturity of open cheques in the direction being viewed. Replaces a
            chart that was labelled for both directions but only had one. */}
        {(() => {
          const now = new Date(); now.setHours(0, 0, 0, 0);
          const buckets = [
            { label: 'Overdue', amount: 0, n: 0 }, { label: 'Next 7 days', amount: 0, n: 0 },
            { label: '8–15 days', amount: 0, n: 0 }, { label: '16–30 days', amount: 0, n: 0 }, { label: '30+ days', amount: 0, n: 0 },
          ];
          items.forEach((p) => {
            if (p.status !== 'pending' && p.status !== 'presented') return;
            const days = Math.ceil((new Date(p.chequeDate).getTime() - now.getTime()) / 86400000);
            const b = days < 0 ? 0 : days <= 7 ? 1 : days <= 15 ? 2 : days <= 30 ? 3 : 4;
            buckets[b].amount += p.amount; buckets[b].n += 1;
          });
          return (
            <section aria-label="Cheque maturity" className="rounded-card border border-outline bg-surface">
              <div className="flex items-center justify-between border-b border-outline px-5 py-3">
                <h2 className="font-display text-[15px] font-semibold text-on-surface">
                  Maturity — {direction === 'receivable' ? 'receivable' : 'payable'} cheques
                </h2>
                <span className="text-body-xs text-on-surface-subtle">Open cheques only</span>
              </div>
              <div className="grid grid-cols-5 divide-x divide-outline">
                {buckets.map((b, i) => (
                  <div key={b.label} className="px-5 py-3">
                    <div className="text-body-xs font-medium uppercase tracking-wider text-on-surface-subtle">{b.label}</div>
                    <div className={`mt-1 font-display text-[18px] font-bold tabular-nums ${i === 0 && b.n ? 'text-danger-text' : 'text-on-surface'}`}>{formatINR(b.amount)}</div>
                    <div className="text-body-xs text-on-surface-subtle">{b.n} cheque{b.n === 1 ? '' : 's'}</div>
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        <div className="flex items-center justify-between gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input className="pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72"
              placeholder="Search cheque no, party, doc no…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-surface-3"
        >
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <div className="h-14 w-14 rounded-xl bg-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-400 flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6" />
              </div>
              <p className="text-sm text-slate-400">No {direction} cheques yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    {['Doc No', 'Cheque', 'Cheque Date', 'Party', 'Bank', 'Amount (₹)', 'Status', ''].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map((p) => (
                    <tr key={p._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-4 py-3 font-mono font-bold text-indigo-700 dark:text-indigo-400 text-xs">{p.docNo}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.chequeNo}</td>
                      <td className={`px-4 py-3 font-mono text-xs ${isOverdue(p) ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                        {new Date(p.chequeDate).toLocaleDateString('en-IN')}
                        {isOverdue(p) && ' (overdue)'}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{p.partyName}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{p.bankName || '—'}</td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-900 dark:text-slate-100">{formatINR(p.amount)}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {p.status === 'pending' && (
                          <>
                            <button onClick={() => act(p, 'present')} className="text-xs font-semibold text-indigo-600 hover:underline mr-3">Present</button>
                            <button onClick={() => act(p, 'cancel')} className="text-xs font-semibold text-slate-400 hover:underline">Cancel</button>
                          </>
                        )}
                        {p.status === 'presented' && (
                          <>
                            <button onClick={() => act(p, 'clear')} className="text-xs font-semibold text-emerald-600 hover:underline mr-3">Clear</button>
                            <button onClick={() => setBounceTarget(p)} className="text-xs font-semibold text-rose-600 hover:underline">Bounce</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>

      {showCreate && (
        <CreatePdcModal direction={direction} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); showToast('Cheque recorded.'); load(); }} />
      )}
      {bounceTarget && (
        <BounceModal pdc={bounceTarget} onClose={() => setBounceTarget(null)} onDone={() => { setBounceTarget(null); showToast(`Cheque ${bounceTarget.chequeNo} marked bounced.`); load(); }} />
      )}
    </div>
  );
}

export default PdcPage;
